import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NotificationWizardResource, NotificationWizardSteps } from '../notification-wizard-type';
import { UsersService } from '../../../users/users.service';
import { GroupsService } from '../../../groups/groups.service';
import { CompaniesService } from '../../../companies/companies.service';

@Component({
    selector: 'app-notification-wizard-filter',
    templateUrl: './notification-wizard-filter.component.html',
    styles: [
        /* language=SCSS */
        `
            .users-grid {
                grid-template-columns: 80px 200px auto 200px;

                @screen sm {
                    grid-template-columns: 80px 200px auto 200px;
                }

                @screen md {
                    grid-template-columns: 80px 200px auto 200px;
                }

                @screen lg {
                    grid-template-columns: 80px 200px auto 200px;
                }
            }
        `,
    ],
    standalone: false
})
export class NotificationWizardFilterComponent implements OnInit, OnDestroy {
    @Output() saveEmitter: EventEmitter<string> = new EventEmitter<string>();
    protected readonly event = event;
    notificationWizard: NotificationWizardResource;

    // User/Group selection mode
    isUserMode = true;
    isGroupMode = false;

    // Search
    searchTerm = '';
    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    // Company filter
    selectedCompanyId: number | null = null;

    // Pagination state
    currentPage = 1;
    totalCount = 0;
    pageSize = 20;
    isLoading = false;

    // Page data
    currentPageUsers: any[] = [];
    currentPageGroups: any[] = [];

    // Selection state (persists across pages)
    selectedUserIds: Set<number> = new Set<number>();
    selectedGroupsMap: Map<number, number> = new Map<number, number>();

    constructor(
        private toastr: ToastrService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _usersService: UsersService,
        private _groupsService: GroupsService,
        private _companiesService: CompaniesService,
        private translateService: TranslateService,
    ) {
        if (sessionStorage.getItem('notificationWizard')) {
            // @ts-ignore
            this.notificationWizard = JSON.parse(sessionStorage.getItem('notificationWizard')) as NotificationWizardResource;
        } else {
            this.notificationWizard = { step: NotificationWizardSteps.Filter };
        }
    }

    ngOnInit(): void {
        this.searchSubject.pipe(
            debounceTime(300),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.loadPage(1);
        });

        this.loadCompanies();
        this.loadPage(1);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadPage(page: number): void {
        this.currentPage = page;
        if (this.isUserMode) {
            this.loadUsers();
        } else {
            this.loadGroups();
        }
    }

    loadUsers(): void {
        this.isLoading = true;
        const filter = this.searchTerm ? [{ 'filter[fulltext]': this.searchTerm }] : undefined;
        this._usersService.getListUsers(
            String(this.currentPage),
            String(this.pageSize),
            filter,
            undefined,
            this.selectedCompanyId || undefined
        ).subscribe({
            next: (response: any) => {
                this.currentPageUsers = Array.isArray(response?.data) ? response.data : [];
                this.totalCount = response?.meta?.total || 0;
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            },
            error: (error: any) => {
                console.error('Error loading users:', error);
                this.toastr.error(this.translateService.instant('section.users.errorLoading'));
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            }
        });
    }

    loadGroups(): void {
        this.isLoading = true;
        const filter: any = {};
        if (this.searchTerm) { filter.name = this.searchTerm; }
        if (this.selectedCompanyId) { filter.companyId = this.selectedCompanyId; }
        this._groupsService.getGroupsPaginated(
            this.currentPage,
            this.pageSize,
            Object.keys(filter).length ? filter : undefined
        ).subscribe({
            next: (response: any) => {
                this.currentPageGroups = Array.isArray(response?.data) ? response.data : [];
                this.totalCount = response?.meta?.total || 0;
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            },
            error: (error: any) => {
                console.error('Error loading groups:', error);
                this.toastr.error(this.translateService.instant('section.groups.errorLoading'));
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            }
        });
    }

    loadCompanies(): void {
        this._companiesService.getCompanies().subscribe({
            next: (response: any) => {
                const companiesData = Array.isArray(response) ? response : (response?.data || []);
                this.notificationWizard.companyList = Array.isArray(companiesData) ? companiesData : [];
                this._changeDetectorRef.markForCheck();
            },
            error: (error: any) => {
                console.error('Error loading companies:', error);
                this.toastr.error(this.translateService.instant('section.companies.errorLoading'));
            }
        });
    }

    setUserMode(): void {
        this.isUserMode = true;
        this.isGroupMode = false;
        this.searchTerm = '';
        this.currentPage = 1;
        this.totalCount = 0;
        this.loadPage(1);
        this._changeDetectorRef.markForCheck();
    }

    setGroupMode(): void {
        this.isUserMode = false;
        this.isGroupMode = true;
        this.searchTerm = '';
        this.currentPage = 1;
        this.totalCount = 0;
        this.loadPage(1);
        this._changeDetectorRef.markForCheck();
    }

    selectCompany(companyId: number | null): void {
        this.selectedCompanyId = companyId;
        this.loadPage(1);
        this._changeDetectorRef.markForCheck();
    }

    onSearchChange(): void {
        this.searchSubject.next(this.searchTerm);
    }

    onUserCheckboxChange(user: any): void {
        if (this.selectedUserIds.has(user.id)) {
            this.selectedUserIds.delete(user.id);
        } else {
            this.selectedUserIds.add(user.id);
        }
        this._changeDetectorRef.markForCheck();
    }

    onGroupCheckboxChange(group: any): void {
        if (this.selectedGroupsMap.has(group.id)) {
            this.selectedGroupsMap.delete(group.id);
        } else {
            const usersCount = parseInt(group.usersCount) || 0;
            this.selectedGroupsMap.set(group.id, usersCount);
        }
        this._changeDetectorRef.markForCheck();
    }

    getSelectedUsersCount(): number {
        return this.selectedUserIds.size;
    }

    getSelectedGroupsCount(): number {
        return this.selectedGroupsMap.size;
    }

    getSelectedGroupsUserCount(): number {
        let total = 0;
        this.selectedGroupsMap.forEach(count => { total += count; });
        return total;
    }

    getUserCompanies(user: any): string {
        if (user.companies && user.companies.length > 0) {
            return user.companies.map((company: any) => company.name).join(', ');
        }
        return '';
    }

    getGroupCompanies(group: any): string {
        if (group.companies && group.companies.length > 0) {
            return group.companies.map((company: any) => company.name).join(', ');
        }
        return '';
    }

    getGroupUserCount(group: any): number {
        if (group.users && Array.isArray(group.users) && group.users.length > 0) {
            return group.users.length;
        }
        return parseInt(group.usersCount) || 0;
    }

    save(): void {
        let userIds: number[];
        let groupIds: number[];
        let userCount: number;

        if (this.isUserMode) {
            userIds = Array.from(this.selectedUserIds);
            groupIds = [];
            userCount = this.selectedUserIds.size;
        } else {
            userIds = [];
            groupIds = Array.from(this.selectedGroupsMap.keys());
            userCount = 0;
            this.selectedGroupsMap.forEach(count => { userCount += count; });
        }

        this.notificationWizard.userIds = userIds;
        this.notificationWizard.groupIds = groupIds;
        this.notificationWizard.companyId = this.selectedCompanyId;
        this.notificationWizard.userCount = userCount;
        this.notificationWizard.step = NotificationWizardSteps.Text;
        sessionStorage.setItem('notificationWizard', JSON.stringify(this.notificationWizard));
        this.saveEmitter.emit('');
    }
}
